/**
 * Collection Filters & Sorting
 * Handles filtering, sorting, and mobile sidebar functionality
 */

class CollectionFilters {
  constructor() {
    this.filterToggle = document.querySelector('[data-filters-toggle]');
    this.filterContent = document.querySelector('[data-filters-content]');
    this.filterClose = document.querySelector('[data-filters-close]');
    this.filterOverlay = document.querySelector('[data-filters-overlay]');
    this.sortSelect = document.querySelector('[data-sort-by]');
    this.priceApplyBtn = document.querySelector('[data-price-apply]');
    this.vendorCheckboxes = document.querySelectorAll('[data-filter-vendor]');
    this.typeCheckboxes = document.querySelectorAll('[data-filter-type]');
    this.clearFilters = document.querySelector('[data-clear-filters]');
    this.quickAddButtons = document.querySelectorAll('[data-quick-add]');

    this.init();
  }

  init() {
    // Mobile sidebar toggle
    if (this.filterToggle) {
      this.filterToggle.addEventListener('click', () => this.openFilters());
    }

    if (this.filterClose) {
      this.filterClose.addEventListener('click', () => this.closeFilters());
    }

    if (this.filterOverlay) {
      this.filterOverlay.addEventListener('click', () => this.closeFilters());
    }

    // Sort dropdown
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (e) => this.handleSort(e));
    }

    // Price apply button
    if (this.priceApplyBtn) {
      this.priceApplyBtn.addEventListener('click', () => this.handlePriceFilter());
    }

    // Vendor checkboxes
    this.vendorCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleVendorFilter());
    });

    // Product type checkboxes
    this.typeCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleTypeFilter());
    });

    // Clear filters
    if (this.clearFilters) {
      this.clearFilters.addEventListener('click', () => this.clearAllFilters());
    }

    // Quick add to cart
    this.quickAddButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleQuickAdd(e));
    });

    // Close sidebar on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.filterContent && this.filterContent.classList.contains('active')) {
        this.closeFilters();
      }
    });

    // Set current sort value from URL
    this.setCurrentSort();

    // Set current filters from URL
    this.setCurrentFilters();
  }

  setCurrentSort() {
    if (!this.sortSelect) return;

    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by');

    if (sortBy) {
      this.sortSelect.value = sortBy;
    }
  }

  setCurrentFilters() {
    const urlParams = new URLSearchParams(window.location.search);

    // Set price filters
    const minPrice = urlParams.get('filter.v.price.gte');
    const maxPrice = urlParams.get('filter.v.price.lte');

    if (minPrice) {
      const minInput = document.querySelector('[data-price-min]');
      if (minInput) minInput.value = minPrice;
    }

    if (maxPrice) {
      const maxInput = document.querySelector('[data-price-max]');
      if (maxInput) maxInput.value = maxPrice;
    }

    // Set vendor filters
    const vendors = urlParams.getAll('filter.p.vendor');
    this.vendorCheckboxes.forEach(checkbox => {
      if (vendors.includes(checkbox.value)) {
        checkbox.checked = true;
      }
    });

    // Set type filters
    const types = urlParams.getAll('filter.p.product_type');
    this.typeCheckboxes.forEach(checkbox => {
      if (types.includes(checkbox.value)) {
        checkbox.checked = true;
      }
    });
  }

  openFilters() {
    this.filterContent.classList.add('active');
    this.filterOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeFilters() {
    this.filterContent.classList.remove('active');
    this.filterOverlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  handleSort(e) {
    const sortValue = e.target.value;
    const url = new URL(window.location.href);

    if (sortValue) {
      url.searchParams.set('sort_by', sortValue);
    } else {
      url.searchParams.delete('sort_by');
    }

    window.location.href = url.toString();
  }

  handlePriceFilter() {
    const minPrice = document.querySelector('[data-price-min]')?.value || '';
    const maxPrice = document.querySelector('[data-price-max]')?.value || '';
    const url = new URL(window.location.href);

    // Remove existing price filters
    url.searchParams.delete('filter.v.price.gte');
    url.searchParams.delete('filter.v.price.lte');

    // Add new price filters if values exist
    if (minPrice) {
      url.searchParams.set('filter.v.price.gte', minPrice);
    }
    if (maxPrice) {
      url.searchParams.set('filter.v.price.lte', maxPrice);
    }

    window.location.href = url.toString();
  }

  handleVendorFilter() {
    const checkedVendors = Array.from(this.vendorCheckboxes)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);

    const url = new URL(window.location.href);

    // Remove existing vendor filters
    url.searchParams.delete('filter.p.vendor');

    // Add selected vendors
    if (checkedVendors.length > 0) {
      checkedVendors.forEach(vendor => {
        url.searchParams.append('filter.p.vendor', vendor);
      });
    }

    window.location.href = url.toString();
  }

  handleTypeFilter() {
    const checkedTypes = Array.from(this.typeCheckboxes)
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);

    const url = new URL(window.location.href);

    // Remove existing type filters
    url.searchParams.delete('filter.p.product_type');

    // Add selected types
    if (checkedTypes.length > 0) {
      checkedTypes.forEach(type => {
        url.searchParams.append('filter.p.product_type', type);
      });
    }

    window.location.href = url.toString();
  }

  clearAllFilters() {
    const url = new URL(window.location.href);

    // Remove all filter parameters
    url.searchParams.delete('filter.v.price.gte');
    url.searchParams.delete('filter.v.price.lte');
    url.searchParams.delete('filter.p.vendor');
    url.searchParams.delete('filter.p.product_type');
    url.searchParams.delete('sort_by');

    window.location.href = url.pathname;
  }

  async handleQuickAdd(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;

    if (!variantId) {
      console.error('Variant ID not found');
      return;
    }

    // Disable button and show loading state
    const originalText = button.textContent;
    button.textContent = 'Ekleniyor...';
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      await response.json();

      // Success feedback
      button.textContent = '✓ Eklendi';
      button.classList.add('added');

      // Update cart count if cart drawer exists
      this.updateCartCount();

      // Reset button after 2 seconds
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        button.classList.remove('added');
      }, 2000);

      // Optional: Show cart drawer or notification
      this.showCartNotification(productTitle);

    } catch (error) {
      console.error('Error adding to cart:', error);
      button.textContent = 'Hata!';

      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartCount = document.querySelector('[data-cart-count]');

      if (cartCount) {
        cartCount.textContent = cart.item_count;
        cartCount.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }

      // Refresh cart drawer if it exists
      if (typeof window.refreshCartDrawer === 'function') {
        window.refreshCartDrawer();
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  showCartNotification(productTitle) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'cart-notification';
    notification.innerHTML = `
      <div class="cart-notification__content">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span><strong>${productTitle}</strong> sepete eklendi</span>
      </div>
    `;

    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 16px 20px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
      z-index: 10000;
      animation: slideInRight 0.3s ease;
    `;

    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionFilters();
  });
} else {
  new CollectionFilters();
}

// Add notification animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInRight {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOutRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(400px);
      opacity: 0;
    }
  }

  .cart-notification__content {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #10b981;
  }

  .cart-notification svg {
    flex-shrink: 0;
  }

  .product-card__quick-add.added {
    background: #10b981 !important;
    color: white !important;
  }

  .product-card__quick-add:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
document.head.appendChild(style);
